// src/components/common/KpiCard.tsx
import React from 'react';
import { fmt, fmtValue } from '../../utils/format';
import { cardShell } from '../../utils/styles';

export interface KpiCardProps {
   containerClasses?: string;
   icon?: React.ReactNode;
   label: string;
   value: string | number;
   sub?: string;
   color?: string;       // Tailwind class OR hex string (e.g. '#f97316')
   wide?: boolean;
   compact?: boolean;    // true = round to k/M (default), false = full number
   active?: boolean;     // dims the card when false
   onClick?: () => void; // makes card a toggle button
}

const KpiCard: React.FC<KpiCardProps> = ({
      containerClasses,
      icon,
      label,
      value,
      sub,
      color = 'text-white',
      wide,
      compact = true,
      active,
      onClick,
   }) => {
   const displayed = typeof value === 'number' ? fmt(value, compact) : fmtValue(value, compact);
   
   const isToggleable = onClick !== undefined;
   const resolvedActive = active ?? true;
   
   const isHex = color?.startsWith('#');
   const valueStyle: React.CSSProperties = isHex
      ? { color: resolvedActive ? color : '#9ca3af' }
      : {};
   const valueClass = !isHex
      ? (resolvedActive ? color : 'text-gray-500')
      : '';
   
   const Wrapper = (isToggleable ? 'button' : 'div') as 'div';
   
   return (
      <Wrapper
         onClick={onClick}
         className={`@container ${cardShell} p-3 md:p-4 flex flex-col gap-0.5 md:gap-1 ${
            isToggleable ? 'cursor-pointer hover:bg-gray-800 transition-colors text-left w-full' : ''
         } ${containerClasses ?? ''} ${wide ? 'col-span-2' : ''}`}
      >
         <div className="flex items-center gap-1.5 text-gray-400 text-[10px] md:text-xs">
            {icon && <span className="text-sm md:text-base">{icon}</span>}
            <span className="truncate">{label}</span>
         </div>
         <div
            className={`text-[clamp(0.5625rem,10cqw,1.5rem)] font-bold font-mono leading-tight truncate ${valueClass}`}
            style={valueStyle}
         >
            {displayed}
         </div>
         {sub && <div className="text-[10px] md:text-xs text-gray-500 font-mono truncate">{sub}</div>}
         {isToggleable && (
            <div
               className={`mt-1 h-0.5 rounded transition-opacity ${resolvedActive ? 'opacity-100' : 'opacity-30'}`}
               style={isHex ? { backgroundColor: color } : {}}
            />
         )}
      </Wrapper>
   );
};

export default KpiCard;
